import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Table, TableBody, TableRow, TableCell, TableContainer, TableHead, Typography, Chip, Tooltip, Divider, LinearProgress, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Crumbs, DataRow, DateDisplay, MonoText, Title, TraxxButton } from 'app/components';
import { API } from 'app/services';
import { snakeToTitle, useAsyncTask, useRouter } from 'app/utils';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Link as BrowserLink } from 'react-router-dom';
import { DownloadRegtankPreview, IndividualMatchDetail, OrganisationMatchDetail } from './components';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3, 3, 8) },
  table: { marginTop: 14, borderRadius: 5 },
  title: { marginTop: 15, textDecoration: 'underline' },
  head: { fontWeight: 'bold', maxWidth: 200 },
  subtitle: { opacity: '70%' },
  tag: { margin: '2px' },
  tooltip: { fontSize: '1rem' },
}));


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
let mounted = false;

function MatchDetailRegtank({ routeType = 'account' }) {
  const classes = useStyles();
  const router = useRouter();
  const { params } = router.match;
  const { enqueueSnackbar } = useSnackbar();

  const access = useSelector((state) => state.profile.access);
  const loading = useSelector((state) => state.layout.loadingTasks);

  const [errorCatcher] = useAsyncTask('getScreening');
  const [runUnresolve] = useAsyncTask('unresolve');

  const [matching, setMatching] = useState(null);
  const isOrganisation = matching?.screen?.type === 'organisation';
  const [matchList, setMatchListing] = useState();
  const [matchRawResult, setMatchRawResult] = useState(null);
  const [printPreview, setPrintPreview] = useState(false);

  const { screening_id, match_id } = params;

  useEffect(() => {
    mounted = true;
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (access?.token) getMatchDetail();
  }, [access]);

  // -------------------------------------------------------------------------
  const getMatchDetail = () => {
    if (!screening_id || !match_id) return enqueueSnackbar('invalid id', { variant: 'error' });

    errorCatcher(async () => {
      const loader = routeType === 'account' ? API.Screening.get_match_detail : API.Admin.get_match;

      const { model } = await loader(screening_id, match_id);
      if (!mounted) return;

      setMatching(model);

      const { content, aml_types, lists, media, peps, sanctions } = model.screen_result_raw || {};

      const parsed = content ? JSON.parse(content) : {};
      const match = parsed.match || {};
      const matchDetails = parsed.matchDetails || {};
      const parsedPeps = peps ? JSON.parse(peps) : {};
      const parsedLists = lists ? JSON.parse(lists) : [];
      const parsedMedia = media ? JSON.parse(media) : [];
      const parsedSanctions = sanctions ? JSON.parse(sanctions) : [];
      const parsedAmlTypes = aml_types ? JSON.parse(aml_types) : [];
      const isOrg = model.screen?.type === 'organisation';
      const rawResult = {
        detail: {
          profile: {
            matching_name: match.name,
            
            person: {
              names: [{ name: match.name }],
              aliases: isOrg
                ? matchDetails.aliases || []
                : parsedPeps.aliases || [],
              nationalities: match.nationality ? [match.nationality] : [],
              dates_of_birth: match.dayOfBirth ? [{ value: `${match.dayOfBirth}-${match.monthOfBirth}-${match.yearOfBirth}`, source: 'Regtank' }] : [],
              addresses: isOrg
                ? matchDetails.addresses || []
                : parsedPeps.addresses || [],
              deceased: parsedPeps.deceased || false,
              reportedAllegation: parsedPeps.reportedAllegation || null,
              associates: (matchDetails.connections || []).map((c) => ({
                type: c.associations || 'Associate',
                name: c.name,
                keywords: c.keywords || [],
              })),
              profileNotes: parsedPeps.profileNotes || [],
            },
            addresses: matchDetails.addresses || [],
          },
        },
        media: parsedMedia,
        lists: parsedLists,
        peps: parsedPeps,
        sanctions: parsedSanctions,
        aml_types: parsedAmlTypes,
      };

      setMatchRawResult(rawResult);
    });
  };

  // -------------------------------------------------------------------------
  const getScreenDetailPath = (id) => (routeType === 'admin' ? `/admin/screenings/${id}/detail` : `/screenings/${id}/detail`);

  const getCaseDetailPath = (id) => (routeType === 'admin' ? `/admin/screenings/cases/${id}/detail` : `/screenings/cases/${id}/detail`);

  const unResolve = () => {
    runUnresolve(async () => {

      await (
        routeType === 'account'
          ? API.Users.unresolve_matches(screening_id, {
              resolve_ids: [match_id],
            })
          : API.Admin.unresolve_matches(screening_id, {
              resolve_ids: [match_id],
            })
      );

      enqueueSnackbar('Match unresolved', {
        variant: 'success',
      });

      getMatchDetail();
    });
  };
  // -------------------------------------------------------------------------
  return (
    <Box className={classes.root}>
      {!printPreview && (
        <>
          {/* BREADCRUMBS */}
          <Crumbs
            current="Detail"
            links={[
              {
                text: 'Screening',
                href: routeType === 'admin' ? '/admin/screenings' : '/screenings',
                key: 'screening',
              },
              {
                text: params.screening_id,
                href: getScreenDetailPath(params.screening_id),
                key: 'screeningId',
              },
            ]}
          />

          <Title
            prefix="Match detail"
            floatButton={
              matching?.profile_retrieved_at && {
                onClick: () => setPrintPreview(true),
                text: 'Preview print document',
              }
            }>
            {matching && 'Powered by Regtank'}
          </Title>

          <Grid spacing={2} container>
            {/* ── LEFT PANEL: match metadata ─────────────────────────────── */}
            <Grid item md={6} xs={12}>
              <TableContainer component={Paper} className={classes.table}>
                {loading.getScreening && <LinearProgress />}
                {!loading.getScreening && matching && (
                  <Table>
                    <TableBody>
                      <DataRow name="Name" value={matching.name} />

                      {(matching.screen?.type === 'simple' || matching.screen?.type === 'passport') && <DataRow name="Gender" value={snakeToTitle(matching.gender || '')} />}

                      <DataRow name="Created date" value={<DateDisplay date={matching.created_at} />} />
                      <DataRow name="Match ID" value={<MonoText>{matching.reference}</MonoText>} />

                      <DataRow
                        name="Screening ID"
                        value={
                          <Link underline="always" component={BrowserLink} to={getScreenDetailPath(matching.screen?.id)}>
                            <MonoText>{matching.screen?.reference}</MonoText>
                          </Link>
                        }
                      />

                      {matching.screen?.case && (
                        <DataRow
                          name="Case ID"
                          value={
                            <Link underline="always" component={BrowserLink} to={getCaseDetailPath(matching.screen.case.id)}>
                              <MonoText>{matching.screen.case.reference}</MonoText>
                            </Link>
                          }
                        />
                      )}

                      <DataRow name="Resolved" value={<DateDisplay date={matching.resolved_at} />} />

                      {matching.resolved_reason && matching.resolved_at && <DataRow name="Resolved reason" value={matching.resolved_reason} />}

                      {matching.resolved_at && (
                        <DataRow
                          name=""
                          value={
                            <TraxxButton loading={loading.unresolve} onSubmit={unResolve}>
                              <Typography>Unresolve</Typography>
                            </TraxxButton>
                          }
                        />
                      )}

                      {matching.profile_retrieved_at && <DataRow name="Profile retrieved date" value={<DateDisplay date={matching.profile_retrieved_at} />} />}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Grid>
            <Grid item md={9} xs={12}>
              {!isOrganisation && <IndividualMatchDetail matchRawResult={matchRawResult} classes={classes} />}

              {isOrganisation && <OrganisationMatchDetail matchRawResult={matchRawResult} classes={classes} />}
            </Grid>
          </Grid>
        </>
      )}
      {printPreview && (
        <DownloadRegtankPreview
          matchList={matchList}
          cancelPrint={() => setPrintPreview(false)}
          detail={matching}
          profile={{
            details: matchRawResult,
            names: matchRawResult?.detail?.profile?.person,
          }}
          isOrganisation={isOrganisation}
        />
      )}
    </Box>
  );
}

export default MatchDetailRegtank;
